import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'
import { coursePeriod } from '../courses/period.js'
import { ApplyModal } from '../components/ApplyModal.jsx'

// Public course catalog. Featured courses show a ★; when registration_end is
// within 5 days a "You have X days to register" badge appears; price_show=0
// courses render the contact-the-teacher text instead of a price.
//
// Each open, non-self-managed course card has a Participate button that opens
// a modal (explicit exception to the forms-are-pages rule) listing the user's
// active participants — picking one enrolls directly (pay_course → Stripe).

export function CoursesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()
  const [courses, setCourses] = useState([])
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalCourse, setModalCourse] = useState(null) // course being applied to
  const [modalMode, setModalMode] = useState('enroll') // 'enroll' | 'waitlist'

  useEffect(() => {
    api.courses.list()
      .then((d) => setCourses(d.courses || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [])

  function openApply(course, mode = 'enroll') {
    if (!authLoading && !user) {
      navigate('/login')
      return
    }
    setModalMode(mode)
    setModalCourse(course)
  }

  const categories = [...new Set(courses.map((c) => c.category_name))].sort()
  const shown = category ? courses.filter((c) => c.category_name === category) : courses

  return (
    <main className="container">
      <h1>{t('courses.title')}</h1>
      <p className="muted">{t('courses.intro')}</p>

      {error && <p className="error">{t('auth.errors.server')}</p>}
      {loading && <p>{t('common.loading')}</p>}

      {!loading && categories.length > 1 && (
        <label style={{ maxWidth: '20rem' }}>
          <span>{t('courses.filter_category')}</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">{t('courses.all_categories')}</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      )}

      {!loading && shown.length === 0 && <p className="muted">{t('courses.empty_state')}</p>}

      <ul className="admin-card-grid">
        {shown.map((c) => (
          <li key={c.id} className="admin-card">
            {c.image_url && (
              <img src={c.image_url} alt="" style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: '6px' }} />
            )}
            <h2>
              <Link to={`/courses/${c.slug}`}>
                {c.featured === 1 && <span title={t('courses.featured')} aria-label={t('courses.featured')}>★ </span>}
                {c.name}
              </Link>
            </h2>
            <p className="muted small">{c.category_name}{c.teacher_name ? ` · ${c.teacher_name}` : ''}</p>
            <p>
              {c.price !== null
                ? <strong>{fmtPrice(c.price)}</strong>
                : <span className="muted">{t('courses.contact_teacher_price')}</span>}
            </p>
            {coursePeriod(t, c.date_start, c.date_end) && (
              <p className="muted small">{coursePeriod(t, c.date_start, c.date_end)}</p>
            )}
            {c.is_open && c.days_to_register !== null && c.days_to_register <= 5 && (
              <p className="error">{t('courses.days_left', { count: c.days_to_register })}</p>
            )}
            {!c.is_open && !c.waitlist_open && (
              <p className="muted small">{t('courses.registration_closed')}</p>
            )}
            {c.waitlist_open && <p className="muted small">{t('courses.full_waitlist')}</p>}
            {c.capacity_left !== null && c.is_open && (
              <p className="muted small">{t('courses.seats_left', { count: c.capacity_left })}</p>
            )}
            {c.is_open && c.managed !== 'self' && (
              <p>
                <button type="button" onClick={() => openApply(c)}>
                  {t('courses.apply')}
                </button>
              </p>
            )}
            {c.waitlist_open && (
              <p>
                <button type="button" onClick={() => openApply(c, 'waitlist')}>
                  {t('courses.waitlist_join')}
                </button>
              </p>
            )}
          </li>
        ))}
      </ul>

      {modalCourse && (
        <ApplyModal
          course={modalCourse}
          mode={modalMode}
          onClose={() => setModalCourse(null)}
        />
      )}
    </main>
  )
}
